<template>
  <SoDialog
    v-model="value"
    :loading="updateProductFlagsMutator.isPending"
    card-title="Are you sure?"
    data-testid="energy-products-modal"
    :max-width="1000"
  >
    <SoAlert
      v-if="isProduction"
      title="Production Environment"
      type="warning"
      data-testid="energy-products-prod-alert"
    >
      Beware: Changes to energy products will affect our customers!
    </SoAlert>
    <SoAlert
      v-else
      title="Staging Environment"
      type="info"
      data-testid="energy-products-staging-alert"
    >
      Beware: Changes to energy products may break automated QA tests!
    </SoAlert>

    <SoErrorBoundary>
      <p class="font-weight-medium mt-6">
        You are about to make the following changes:
      </p>
      <SoDataTable
        :headers="headers"
        :items="changesTableItems"
        :has-pagination="false"
        :searchable="false"
        class="mt-4 mb-2 border rounded"
        data-testid="energy-products-modal-table"
      >
        <template #item.changes="{ internalItem }">
          <p v-for="(change, index) in internalItem.raw.changes" :key="index">
            {{ change }}
          </p>
        </template>
      </SoDataTable>
    </SoErrorBoundary>
    <SoAlert
      v-if="isUpdated"
      title="Changes saved"
      type="success"
      class="mt-4"
      data-testid="energy-products-modal-success-alert"
    />

    <SoAlert
      v-if="updateProductFlagsMutator.error"
      title="Failed to save changes"
      type="error"
      class="mt-4"
      data-testid="energy-products-modal-error-alert"
    />
    <SoCheckbox
      v-if="isProduction && !updateProductFlagsMutator.data"
      v-model="confirmed"
      label="I understand these changes will affect our customers"
      class="mt-6"
      data-testid="energy-products-modal-confirm-checkbox"
    />
    <div class="d-flex justify-end mt-8">
      <template v-if="updateProductFlagsMutator.data">
        <SoButton
          title="Close"
          large
          data-testid="energy-products-modal-close-btn"
          @click="value = false"
        />
      </template>
      <template v-else>
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          data-testid="energy-products-modal-cancel-btn"
          @click="value = false"
        />
        <SoButton
          title="Save"
          type="success"
          large
          :loading="updateProductFlagsMutator.isPending"
          :disabled="isProduction && !confirmed"
          data-testid="energy-products-modal-save-btn"
          @click="saveChanges()"
        />
      </template>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { IProduct } from "@/models/admin/product/product.types"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import { yesNo } from "@/helpers/stringHelpers"
  import { isProduction } from "@/helpers/envHelper"
  import {
    Product_Variant_Gas_Set_Input,
    Product_Variant_Elec_Set_Input,
    Product_Variant_Elec_Updates,
    Product_Variant_Gas_Updates,
    Product_Elec_Updates,
  } from "@/api/hasura/generated/graphql"
  import { ProductMutator } from "@/mutators/product/ProductMutator"

  export type EnergyProductListForm = {
    [key: string]: {
      update: Partial<IProduct>
      original: IProduct
    }
  }

  const headers = [
    {
      title: "Product Name",
      key: "name",
      sortable: true,
    },
    {
      title: "Changes",
      key: "changes",
      sortable: false,
    },
  ]

  const propertyNames = {
    isActiveForEnrolment: "Active For Enrolment",
    isActiveForRenewal: "Active For Renewal",
    isRecommendedForEnrolment: "Recommend For Enrolment",
    isRecommendedForRenewal: "Recommend For Renewal",
    requiresSmartCharging: "Requires Smart Charging",
  }

  const columnNames = {
    isActiveForEnrolment: "active_for_enrolment",
    isActiveForRenewal: "active_for_renewal",
    isRecommendedForEnrolment: "recommend_for_enrolment",
    isRecommendedForRenewal: "recommend_for_renewal",
  }

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoButton,
      SoCheckbox,
      SoAlert,
      SoDataTable,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      changes: {
        type: Object as PropType<EnergyProductListForm>,
        default: null,
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        headers,
        confirmed: false,
        updateProductFlagsMutator: ProductMutator.update(),
      }
    },
    computed: {
      isProduction,
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          if (!value) {
            if (!value && this.updateProductFlagsMutator.isSuccess) {
              this.$emit("success")
              this.updateProductFlagsMutator.reset()
            }
            this.confirmed = false
            this.$emit("update:modelValue", value)
          }
        },
      },
      changesTableItems() {
        return Object.values(this.changes).map((change) => {
          const changes: string[] = []

          for (const key in propertyNames) {
            if (key in change.update) {
              const name = propertyNames[key]
              const oldVal = yesNo(change.original[key])
              const newVal = yesNo(change.update[key])
              changes.push(`${name}: ${oldVal} → ${newVal}`)
            }
          }

          return {
            name: change.original.name,
            changes,
          }
        })
      },
      isUpdated() {
        return this.updateProductFlagsMutator.isSuccess
      },
    },
    methods: {
      saveChanges() {
        this.updateProductFlagsMutator.mutate(
          this.formatMutationVariables(this.changes),
        )
      },
      formatMutationVariables(changes: EnergyProductListForm) {
        const formattedVariables = {
          elec_updates: [] as Product_Variant_Elec_Updates[],
          gas_updates: [] as Product_Variant_Gas_Updates[],
          smart_charging_updates: [] as Product_Elec_Updates[],
        }

        Object.values(changes).forEach((change) => {
          const { requiresSmartCharging, ...variantChanges } = change.update

          // requires_smart_charging lives on product_elec, keyed per product
          if (requiresSmartCharging !== undefined) {
            formattedVariables.smart_charging_updates.push({
              where: { product: { id: { _eq: change.original.id } } },
              _set: { requires_smart_charging: requiresSmartCharging },
            })
          }

          if (!Object.keys(variantChanges).length) return

          const propertyName = change.original.name?.includes("Gas")
            ? "gas_updates"
            : "elec_updates"

          const toUpdate = Object.entries(variantChanges).reduce(
            (entries, [key, value]) => {
              entries[columnNames[key]] = value
              return entries
            },
            {} as
              Product_Variant_Elec_Set_Input | Product_Variant_Gas_Set_Input,
          )

          formattedVariables[propertyName].push({
            where: {
              product_variant: {
                product_id: {
                  _eq: change.original.id,
                },
              },
            },
            _set: toUpdate,
          })
        })
        return formattedVariables
      },
    },
  })
</script>
